import { noop } from 'betterplace-utils'
import type {
  BoxSizesKey,
  DeepReadonly,
  ISubscribable,
  RowComponentProps,
  RowRange,
  VirtualScrollItemStyle,
  VirtualiserRenderProps,
  VirtualiserSnapshot,
} from './types'

/**
 * Virtualiser class that manages virtual scrolling for large lists.
 * Handles row positioning, height calculation, and visible range management.
 */
export class Virtualiser implements ISubscribable<VirtualiserSnapshot> {
  /**
   * Extracts the size (width or height) from a ResizeObserverEntry based on the specified box model and size type.
   * @param entry - The ResizeObserverEntry to extract size from
   * @param box - The box model to use (e.g., 'contentBoxSize', 'borderBoxSize')
   * @param sizeType - The size dimension to extract ('inlineSize' for width, 'blockSize' for height)
   * @returns The extracted size in pixels, or undefined if not available
   */
  static extractSize(
    entry: ResizeObserverEntry,
    box: BoxSizesKey,
    sizeType: 'inlineSize' | 'blockSize'
  ): number | undefined {
    const boxSize = entry[box] as ResizeObserverSize[] | ResizeObserverSize | undefined
    if (Array.isArray(boxSize) && boxSize.length > 0) {
      const result = boxSize[0]?.[sizeType]
      return result
    }
    if (boxSize && typeof boxSize === 'object' && sizeType in boxSize) {
      const result = (boxSize as ResizeObserverSize)[sizeType]
      return result
    }
    const result = sizeType === 'inlineSize' ? entry.contentRect.width : entry.contentRect.height
    return result
  }

  public static getDiffMap(
    map1: Map<number, number>,
    map2: Map<number, number>
  ): Map<number, [old: number | undefined, new: number | undefined]> {
    const diffMap = new Map<number, [old: number | undefined, new: number | undefined]>()
    for (const [key, value] of map1) {
      const newVal = map2.get(key)
      if (newVal === value) continue
      diffMap.set(key, [value, newVal])
    }
    for (const [key, value] of map2) {
      if (map1.has(key)) continue
      diffMap.set(key, [undefined, value])
    }
    return diffMap
  }

  static compareRange(range1: RowRange, range2: RowRange) {
    const [startIndex1, endIndex1, currentRowIndex1] = range1
    const [startIndex2, endIndex2, currentRowIndex2] = range2
    return startIndex1 === startIndex2 && endIndex1 === endIndex2 && currentRowIndex1 === currentRowIndex2
  }
  /** The container element that holds the virtualized list */
  private container: HTMLDivElement | null = null
  /** The current width of the container in pixels */
  private containerWidth: number | null = null
  /** The index of the row currently at the top of the visible viewport */
  private currentRowIndex: number = 0
  /** Whether a static row height has been explicitly set */
  private get staticRowHeight() {
    return !!this._rowHeight
  }
  /** The gap between rows in pixels */
  private gap: number = 0
  /** Map of row index to measured height in pixels */
  private heightMap = new Map<number, number>()
  /** Array of row component props for the currently visible range */
  private items: RowComponentProps[] = []
  /** Maximum number of rows in the virtualized list */
  private maxRows: number = 0
  /** Flag indicating that positions and heights need to be recalculated */
  private needsRecalculation: boolean = false
  /** ResizeObserver instance for monitoring container and row size changes */
  private observer: ResizeObserver | null = null
  /** Current scroll offset that has been applied */
  private offset: number = 0
  /** Requested scroll offset that may not yet be applied */
  private _requestedOffset: number = 0
  /** Difference between requested and applied offset */
  private get offsetDiff() {
    return this._requestedOffset - this.offset
  }
  /** Callback function invoked when the visible range changes */
  private onScrollCallback: ((range: RowRange) => void) | undefined = undefined
  /** Number of rows to render outside the visible viewport for smoother scrolling */
  private overscanRows: number = 0
  /** Map of row index to vertical position in pixels from the top */
  private positionMap = new Map<number, number>()
  /** Current visible range [startIndex, endIndex, currentRowIndex] */
  private range: RowRange = [0, 0, 0]
  /** Flag indicating if the visible range has changed */
  private rangeHasChanged = false
  /** Callback function to trigger a re-render */
  private _rerender: () => void = noop
  /** Reverse map from DOM element to row index for ResizeObserver callbacks */
  private reverseRowRefs = new Map<HTMLDivElement, number>()

  /** Median height calculated from measured row heights */
  private _medianRowHeight: number = 0
  /** Height measured from a spy element (first row) */
  private _spyRowHeight: number = 0
  /** Explicitly set row height, or 0 if using dynamic heights */
  private _rowHeight: number = 0
  /** Effective row height, using explicit height, median, or spy height in that order */
  private get rowHeight() {
    if (this._rowHeight) return this._rowHeight
    if (this._medianRowHeight) return this._medianRowHeight
    if (this._spyRowHeight) return this._spyRowHeight
    return 0
  }

  /** Map of row index to DOM element reference */
  private rowRefs = new Map<number, HTMLDivElement>()
  /** Flag indicating that fallback heights need to be calculated */
  private needsFallbackHeightsCalculation = false
  /** Flag indicating that full calculation of item positions is needed (from 0 to the end of range) */
  private needsFullCalculation = false
  /** Number of rows visible in the viewport */
  private visibleRowCount: number = 0
  /** Current snapshot of virtualiser state for subscribers */
  private snapshot: VirtualiserSnapshot = { items: [], totalHeight: 0, containerHeight: 0 }
  private _refCallbackCache = new Map<number, (element: HTMLDivElement | null) => void>()

  /**
   * Gets the current internal state of the virtualiser for debugging purposes.
   * @returns A readonly snapshot of all internal state
   */
  public getState() {
    const state = {
      container: this.container,
      containerWidth: this.containerWidth,
      currentRowIndex: this.currentRowIndex,
      explicitlySetRowHeight: this.staticRowHeight,
      gap: this.gap,
      heightMap: this.heightMap,
      maxRows: this.maxRows,
      needsFallbackHeightsCalculation: this.needsFallbackHeightsCalculation,
      needsRecalculation: this.needsRecalculation,
      needsFullCalculation: this.needsFullCalculation,
      observer: this.observer,
      offset: this.offset,
      offsetDiff: this.offsetDiff,
      overscanRows: this.overscanRows,
      positionMap: this.positionMap,
      range: this.range,
      rangeHasChanged: this.rangeHasChanged,
      rerender: this._rerender,
      reverseRowRefs: this.reverseRowRefs,
      rowHeight: this.rowHeight,
      staticRowHeight: this.staticRowHeight,
      rowRefs: this.rowRefs,
      snapshot: this.snapshot,
      visibleRowCount: this.visibleRowCount,
    }
    return state as DeepReadonly<typeof state>
  }

  public rerender() {
    this._rerender()
  }

  /**
   * Sets the container width and triggers recalculation if changed.
   * @param width - The new container width in pixels
   */
  public setContainerWidth(width: number) {
    if (width === this.containerWidth) return
    this.containerWidth = width
    this.needsRecalculation = true
    // do not calculate fallback heights if the row height is explicitly set
    this.needsFallbackHeightsCalculation = !this.staticRowHeight
    this.setRowHeight(this.rowHeight)
  }

  /**
   * Sets the height for a specific row and triggers recalculation.
   * @param index - The row index
   * @param height - The row height in pixels
   */
  public setIndividualRowHeight(index: number, height: number) {
    if (height === this.heightMap.get(index)) return
    this.heightMap.set(index, height)
    this.needsRecalculation = true
  }

  /**
   * Adds a DOM element reference for a row and starts observing it for size changes.
   * @param element - The DOM element representing the row
   * @param index - The row index
   */
  private addElement(element: HTMLDivElement, index: number) {
    const old = this.rowRefs.get(index)
    if (old === element) return
    this.rowRefs.set(index, element)
    this.reverseRowRefs.set(element, index)
    if (this.staticRowHeight) return
    this.heightMap.set(index, Math.round(element.clientHeight))
    this.observer?.observe(element, { box: 'border-box' })
  }

  /**
   * Removes a DOM element reference for a row and stops observing it.
   * @param index - The row index to remove
   */
  private removeElement(index: number) {
    const ele = this.rowRefs.get(index)
    if (!ele) return
    this.rowRefs.delete(index)
    const reversedIndex = this.reverseRowRefs.get(ele)
    if (reversedIndex === undefined || reversedIndex !== index) return
    this.reverseRowRefs.delete(ele)
    this.observer?.unobserve(ele)
  }

  /**
   * Calculates the median height from all measured row heights.
   * @returns The median row height in pixels, or 0 if no heights are available
   */
  public getMedianRowHeight(): number {
    if (!this.heightMap.size) return 0
    const heights = Array.from(this.heightMap.values()).sort((a, b) => a - b)
    const half = Math.floor(heights.length / 2)
    const result = heights.length % 2 ? heights[half]! : Math.round((heights[half - 1]! + heights[half]!) / 2)
    return result
  }

  /**
   * Sets the row height based on the spy element (first row) if available.
   */
  private _setRowHeightToSpyHeight() {
    if (!this.container) return
    const spy = this.container.firstElementChild?.firstElementChild as HTMLDivElement | undefined | null
    if (!spy) return
    const height = Math.round(spy.clientHeight)
    if (height === this._spyRowHeight) return
    this._spyRowHeight = height
    this.needsRecalculation = true
  }

  /**
   * Sets the row height to the median of all measured heights.
   */
  private _setRowHeightToMedianHeight() {
    if (this.heightMap.size === 0) return
    const medianHeight = this.getMedianRowHeight()
    if (medianHeight) {
      this.needsFallbackHeightsCalculation = false
    }
    if (medianHeight === this._medianRowHeight) return
    this._medianRowHeight = medianHeight
    this.needsRecalculation = true
  }

  /**
   * Sets fallback row height using median or spy height when explicit height is not available.
   */
  private setFallbackRowHeight() {
    if (!this.needsFallbackHeightsCalculation) return
    this._setRowHeightToMedianHeight()
    this._setRowHeightToSpyHeight()
  }

  /**
   * Sets the row height explicitly, or enables fallback calculation if null/undefined.
   * @param height - The row height in pixels, or null/undefined to use dynamic heights
   */
  public setRowHeight(height: number | null | undefined) {
    const rowHeight = height ?? 0
    if (rowHeight !== this._rowHeight) {
      this._rowHeight = rowHeight
      this.needsRecalculation = true
    }
    if (!rowHeight) {
      this.needsFallbackHeightsCalculation = true
    }
    this.setFallbackRowHeight()
  }

  /**
   * Sets the number of rows visible in the viewport and updates the visible range.
   * @param count - The number of visible rows
   */
  public setVisibleRowCount(count: number) {
    if (count === this.visibleRowCount) return
    this.visibleRowCount = count
    this.setRangeFromIndex(this.currentRowIndex)
  }

  /**
   * Sets the number of rows to render outside the visible viewport for smoother scrolling.
   * @param count - The number of overscan rows
   */
  public setOverscanRows(count: number) {
    if (count === this.overscanRows) return
    this.overscanRows = count
    this.setRangeFromIndex(this.currentRowIndex)
  }

  /**
   * Sets the maximum number of rows in the list, or calculates it automatically if null/undefined.
   * @param count - The maximum number of rows, or null/undefined for auto-calculation
   */
  public setMaxRows(count: number | null | undefined) {
    const maxRows_ = count ?? this.currentRowIndex + (this.visibleRowCount + this.overscanRows) * 2 + 1
    if (maxRows_ === this.maxRows) return
    this.maxRows = maxRows_
    this.setRangeFromIndex(this.currentRowIndex)
  }

  /**
   * Sets the gap between rows and triggers recalculation.
   * @param gap - The gap size in pixels
   */
  public setGap(gap: number) {
    if (gap === this.gap) return
    this.gap = gap
    this.needsRecalculation = true
  }

  /**
   * Sets the container element and starts observing it for size changes.
   * @param container - The container DOM element, or null to clear
   */
  public setContainer(container: HTMLDivElement | null) {
    if (this.container) {
      this.observer?.unobserve(this.container)
    }
    this.container = container
    if (!container) return
    this.observer?.observe(container)
    this.setContainerWidth(Math.round(container.clientWidth))
    this.needsRecalculation = true
  }

  /**
   * Calculates a fallback row index based on scroll position when position map is unavailable.
   * @param scrollPos - The scroll position in pixels
   * @returns The estimated row index
   */
  private _calculateFallBackRowIndex(scrollPos: number): number {
    const result = Math.floor(scrollPos / (this.rowHeight + this.gap))
    return result
  }

  /**
   * Calculates the row index at a given scroll position using the position map or fallback calculation.
   * @param scrollPos - The scroll position in pixels
   * @returns The row index at the scroll position
   */
  public getRowIndexFromScrollPos(scrollPos: number): number {
    const estimatedRow = this.currentRowIndex
    const estimatedRowPos = this.positionMap.get(estimatedRow)

    if (typeof estimatedRowPos !== 'number') return this._calculateFallBackRowIndex(scrollPos)

    if (estimatedRowPos === scrollPos) return estimatedRow

    const length = this.positionMap.size

    if (estimatedRowPos > scrollPos) {
      for (let i = estimatedRow - 1; i >= 0; i--) {
        const pos = this.positionMap.get(i)
        if (typeof pos === 'number' && pos <= scrollPos) {
          return i
        }
      }
      return 0
    }

    for (let i = estimatedRow + 1; i < length; i++) {
      const pos = this.positionMap.get(i)
      if (typeof pos === 'number' && pos > scrollPos) {
        return i - 1
      }
    }

    return this._calculateFallBackRowIndex(scrollPos)
  }

  /**
   * Updates the current row index based on scroll position and recalculates the visible range.
   * @param scrollPos - The scroll position in pixels
   */
  public setRowIndexFromScrollPos(scrollPos: number) {
    const newRowIndex = this.getRowIndexFromScrollPos(scrollPos)
    if (newRowIndex === this.currentRowIndex) return
    this.currentRowIndex = newRowIndex
    this.setRangeFromIndex(newRowIndex)
  }

  public getRangeFromIndex(rowIndex: number) {
    const startIndex = Math.max(0, rowIndex - this.overscanRows)
    const endIndex = Math.min(
      (this.maxRows || 1) - 1, // don't render past the end of the list
      rowIndex + this.visibleRowCount + this.overscanRows
    )
    return [startIndex, endIndex, rowIndex] as RowRange
  }

  /**
   * Sets the visible range of rows based on the current row index, including overscan rows.
   * @param rowIndex - The row index at the top of the viewport
   */
  public setRangeFromIndex(rowIndex: number) {
    const newRange = this.getRangeFromIndex(rowIndex)
    if (Virtualiser.compareRange(newRange, this.range)) {
      this.rangeHasChanged = false
      return
    }
    this.onScrollCallback?.(newRange)
    this.range = newRange
    this.needsRecalculation = true
    this.rangeHasChanged = true
  }

  /**
   * Calculates the scroll position of the container based on the offset difference.
   * Takes into account expanded items that will dissapear from DOM after the offset is applied.
   * @param offsetDiff - The offset difference in pixels
   * @returns The position in pixels
   */
  public calcOffsetScrollPosition(offsetDiff: number) {
    let position = offsetDiff * (this.rowHeight + this.gap)
    const [startIndex] = this.range
    for (let i = startIndex; i < Math.min(startIndex + offsetDiff, this.maxRows); i++) {
      const heightDiff = (this.heightMap.get(i) ?? this.rowHeight) - this.rowHeight
      position += heightDiff
    }
    return position
  }

  /**
   * Applies the requested scroll offset to the container if there is a difference.
   */
  public applyOffset() {
    if (!this.offsetDiff) return
    if (!this.container || !this.snapshot.totalHeight) return
    this.reapplyRefs(this.range)
    const position = this.calcOffsetScrollPosition(this.offsetDiff)
    if (!position || position > this.snapshot.totalHeight) return
    this.container.scrollBy({ top: position, behavior: 'instant' })
    this.offset = this._requestedOffset

    this.needsFullCalculation = true
  }

  /**
   * Sets the requested scroll offset and applies it immediately.
   * @param offset - The scroll offset in pixels
   */
  public setOffset(offset: number) {
    this._requestedOffset = offset
    this.applyOffset()
  }

  /**
   * Handles scroll events by updating the row index and recalculating positions.
   * @param scrollTop - The current scroll position in pixels
   */
  public onScroll(scrollTop: number) {
    this.setRowIndexFromScrollPos(scrollTop)
    this.calculate()
  }

  /**
   * Gets the CSS style object for positioning a row at a specific index.
   * @param index - The row index
   * @returns The style object with absolute positioning and transform
   */
  public getItemStyle(index: number): VirtualScrollItemStyle {
    const height = this.heightMap.get(index) ?? this.rowHeight
    const position = this.positionMap.get(index) ?? 0
    const result = {
      position: 'absolute',
      inset: 0,
      height: `${height}px`,
      transform: `translateY(${position}px)`,
    } as VirtualScrollItemStyle
    return result
  }

  /**
   * Calculates the vertical positions for all rows in the visible range.
   * @returns True if any positions have changed
   */
  public calculateItemPositions(shouldDoFullCalculation: boolean = false): boolean {
    // position of every row is the sum of the height of the previous rows + the gap, calculate for all visible rows (startIndex to endIndex)
    let positionsHaveChanged = false
    if (!this.rowHeight) return false
    this.positionMap.set(0, 0)
    const [startIndex, endIndex] = this.range
    const currentStartIndex = shouldDoFullCalculation ? 0 : startIndex
    let cumulative = this.positionMap.get(currentStartIndex) ?? (this.rowHeight + this.gap) * currentStartIndex
    for (let i = currentStartIndex; i <= endIndex; i++) {
      const height = this.heightMap.get(i) ?? this.rowHeight
      const nextIndex = i + 1
      cumulative += height + this.gap
      const newPosition = cumulative
      const currentPosition = this.positionMap.get(nextIndex)
      const positionHasChanged = newPosition !== currentPosition
      positionsHaveChanged ||= positionHasChanged
      this.positionMap.set(nextIndex, newPosition)
    }
    return positionsHaveChanged
  }

  /**
   * Calculates the total height of the virtualized list based on all rows.
   * @returns True if the total height has changed
   */
  public calculateTotalHeight(): boolean {
    const lastIndex = this.maxRows - 1
    const lastPosition = this.positionMap.get(lastIndex) ?? 0
    const lastHeight = this.heightMap.get(lastIndex) ?? this.rowHeight
    const result = lastPosition ? lastPosition + lastHeight + this.gap : (this.rowHeight + this.gap) * this.maxRows
    if (result === this.snapshot.totalHeight) return false
    this.snapshot = {
      ...this.snapshot,
      totalHeight: result,
    }
    return true
  }

  /**
   * Calculates the container height based on visible row count.
   * @returns True if the container height has changed
   */
  public calculateContainerHeight(): boolean {
    const result = this.visibleRowCount * (this.rowHeight + this.gap)
    if (result === this.snapshot.containerHeight) return false
    this.snapshot = {
      ...this.snapshot,
      containerHeight: result,
    }
    return true
  }

  /**
   * Calculates and updates the items array for the visible range.
   * @returns True if items have changed
   */
  public calculateItems(): boolean {
    // position of every row is the sum of the height of the previous rows + the gap, calculate for all visible rows (startIndex to endIndex)
    const positionHasChanged = this.calculateItemPositions(this.needsFullCalculation)
    this.needsFullCalculation = false
    const itemsHaveChanged = positionHasChanged || this.rangeHasChanged
    // if the positions have changed, we need to re-create the items within the range
    if (!itemsHaveChanged) return false
    const items: RowComponentProps[] = []
    for (let i = this.range[0]; i <= this.range[1]; i++) {
      const style = this.getItemStyle(i)
      const item = { index: i, ref: this.getRefCallback(i), style }
      items.push(item)
    }
    this.items = items
    this.snapshot = {
      ...this.snapshot,
      items: this.items,
    }
    this.rangeHasChanged = false
    return true
  }

  /**
   * Main calculation method that updates positions, heights, and triggers re-render if needed.
   */
  public calculate() {
    let rowHeightHasChanged = false
    if (this.needsFallbackHeightsCalculation) {
      this.setFallbackRowHeight()
      rowHeightHasChanged = !this.needsFallbackHeightsCalculation
      this.needsRecalculation ||= rowHeightHasChanged
    }
    if (!this.needsRecalculation && !this.offsetDiff) return
    this.needsRecalculation = false
    let hasChanged = false
    const itemsHaveChanged = this.calculateItems()
    // apply offset before calculating total height, as totalHeight will only be applied on the next render
    this.applyOffset()
    const totalHeightChanged = this.calculateTotalHeight()
    const containerHeightChanged = this.calculateContainerHeight()
    hasChanged ||= itemsHaveChanged
    hasChanged ||= totalHeightChanged
    hasChanged ||= containerHeightChanged
    hasChanged ||= rowHeightHasChanged
    if (!hasChanged) return
    this.rerender()
  }

  /**
   * Handles ResizeObserver callbacks for container and row size changes.
   * @param entries - Array of ResizeObserverEntry objects
   */
  public onResize(entries: ResizeObserverEntry[]) {
    for (const entry of entries) {
      if (entry.target === this.container) {
        const width = Math.round(Virtualiser.extractSize(entry, 'contentBoxSize', 'inlineSize') ?? 0)
        if (width && width !== this.containerWidth) {
          this.setContainerWidth(width)
        }
        continue
      }
      const index = this.reverseRowRefs.get(entry.target as HTMLDivElement)
      if (typeof index !== 'number') return
      const height = Math.round(Virtualiser.extractSize(entry, 'borderBoxSize', 'blockSize') ?? 0)
      if (!height || height === this.heightMap.get(index)) return
      this.setIndividualRowHeight(index, height)
    }
    this.calculate()
  }

  /**
   * Creates a new Virtualiser instance.
   */
  constructor() {
    this._rerender = noop
  }

  /**
   * Initializes the ResizeObserver and sets up observation for size changes.
   * Needs to be called before using the virtualiser.
   */
  public init() {
    if (this.observer) return
    this.observer = new ResizeObserver((entries) => {
      this.onResize(entries)
    })
    this.needsRecalculation = true
    this.needsFallbackHeightsCalculation = true
  }

  /**
   * Creates a ref callback function for a row element that handles adding/removing element references.
   * @param i - The row index
   * @returns A callback function to be used as a ref
   */
  private getRefCallback(i: number) {
    const cached = this._refCallbackCache.get(i)
    if (cached) return cached
    const cb = (element: HTMLDivElement | null) => {
      if (element) {
        const res = this.addElement(element, i)
        return res
      }
      return this.removeElement(i)
    }
    this._refCallbackCache.set(i, cb)
    return cb
  }

  /**
   * Reapplies refs for the given range.
   * @param range - The range to reapply refs for
   */
  public reapplyRefs(range: RowRange) {
    const oldRefs = new Map(this.rowRefs)
    for (let i = range[0]; i <= range[1]; i++) {
      const ele = oldRefs.get(i)
      const refCb = this.getRefCallback(i)
      refCb(null)
      if (!ele) continue
      refCb(ele)
    }
  }

  /**
   * Cleans up resources by disconnecting observers and clearing all references.
   * Unsubscribes from the subscription callback
   */
  public tearDown() {
    this.observer?.disconnect()
    this.observer = null
    this.container = null
    this.containerWidth = null
    this.rowRefs.clear()
    this.reverseRowRefs.clear()
    this.heightMap.clear()
    this.positionMap.clear()
    this._rerender = noop
  }

  /**
   * Main render method that updates all virtualiser settings and triggers calculation.
   * Order of calls inside onRender is important
   * @param props - Render props containing all configuration options
   */
  public onRender(props: VirtualiserRenderProps) {
    const { offset, gap, overscanRows, visibleRowCount, container, onScroll, rowHeight, maxRows } = props
    this.onScrollCallback = onScroll
    this.setContainer(container)
    this.setVisibleRowCount(visibleRowCount)
    this.setOverscanRows(overscanRows || visibleRowCount)
    this.setRowHeight(rowHeight)
    this.setMaxRows(maxRows)
    this.setGap(gap ?? 0)
    this.setOffset(offset ?? 0)
    this.calculate()
  }

  /**
   * Subscribes to virtualiser state changes and initializes the observer.
   * Handles a single subscriber
   * @param onStoreChange - Callback function invoked when state changes
   * @returns A cleanup function to unsubscribe
   */
  public subscribe(onStoreChange: () => void) {
    this._rerender = onStoreChange
    this.init()
    return this.tearDown.bind(this)
  }

  /**
   * Gets the current snapshot of virtualiser state for subscribers.
   * @returns The current snapshot containing items, totalHeight, and containerHeight
   */
  public getSnapshot() {
    return this.snapshot
  }
}

/**
 * Factory function that creates a virtualiser store with bound methods. Exposes methods needed to use useSyncExternalStore.
 * @returns An object with onRender, onScroll, subscribe, and getSnapshot methods
 */
export default function createVirtualiserStore() {
  const virtualiser = new Virtualiser()
  return {
    onRender: virtualiser.onRender.bind(virtualiser),
    onScroll: virtualiser.onScroll.bind(virtualiser),
    subscribe: virtualiser.subscribe.bind(virtualiser),
    getSnapshot: virtualiser.getSnapshot.bind(virtualiser),
  }
}
